import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

const placeholders = [
  { emoji:'🙌', label:'Sunday Worship' },{ emoji:'✝', label:'Prayer Service' },
  { emoji:'🕊️', label:'Holy Spirit' },{ emoji:'🎵', label:'Praise & Worship' },
  { emoji:'🙏', label:'Daily Prayer' },{ emoji:'📖', label:'Bible Study' },
  { emoji:'⚡', label:'Youth Service' },{ emoji:'👶', label:"Children's Church" },
];

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);
  const timerRef = useRef(null);

  const fetchImages = async () => {
    const { data } = await supabase.storage.from('gallery').list('', { sortBy: { column: 'created_at', order: 'desc' } });
    if (data && data.length > 0) {
      const imageExts = ['.jpg','.jpeg','.png','.gif','.webp','.bmp','.heic','.heif'];
      const urls = data
        .filter(f => {
          const name = (f.name || '').toLowerCase();
          return (
            name !== '.emptyfolderplaceholder' &&
            !name.startsWith('pastors') &&
            imageExts.some(ext => name.endsWith(ext))  // only image files
          );
        })
        .map(f => ({
          src: supabase.storage.from('gallery').getPublicUrl(f.name).data.publicUrl,
          name: f.name,
        }));
      setImages(urls);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchImages();
    const update = () => {
      if (window.innerWidth < 600) setVisibleCount(1);
      else if (window.innerWidth < 900) setVisibleCount(2);
      else setVisibleCount(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const hasPhotos = images.length > 0;
  const items = hasPhotos ? images : placeholders;
  const total = items.length;

  const goTo = useCallback((index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(((index % total) + total) % total);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, total]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (isPaused || total === 0) return;
    timerRef.current = setInterval(next, 3500);
    return () => clearInterval(timerRef.current);
  }, [next, isPaused, total]);

  const touchStartX = useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
    touchStartX.current = null;
  };

  const visibleIndices = [];
  for (let i = 0; i < Math.min(visibleCount, total); i++) {
    visibleIndices.push((current + i) % total);
  }

  const btnStyle = (side) => ({
    position: 'absolute', [side]: -22, top: '50%', transform: 'translateY(-50%)',
    width: 44, height: 44, borderRadius: '50%',
    background: 'rgba(13,27,62,0.95)', border: '1px solid rgba(201,168,76,0.4)',
    color: 'var(--primary)', fontSize: '1.1rem', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)', transition: 'all 0.25s', zIndex: 10,
  });

  return (
    <section id="gallery" className="section" style={{ background: 'var(--bg-deep)', overflow: 'hidden' }}>
      <div className="container">
        <div style={{ textAlign: 'center' }}>
          <span className="section-tag">✦ Life at Zion</span>
          <h2 className="section-title">Church <span>Gallery</span></h2>
          <div className="divider center" />
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--primary)', padding: '40px' }}>Loading gallery… 🙏</div>
        ) : (
          <>
            <div style={{ position: 'relative' }}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(visibleCount, total)},1fr)`, gap: 16, transition: 'all 0.5s ease' }}>
                {visibleIndices.map((idx, i) => {
                  const item = items[idx];
                  return (
                    <div key={`${idx}-${i}`} style={{ aspectRatio: '4/3', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(201,168,76,0.25)', background: 'linear-gradient(135deg,rgba(201,168,76,0.08),rgba(13,27,62,0.9))', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', transform: isAnimating ? 'scale(0.98)' : 'scale(1)', transition: 'transform 0.5s ease' }}>
                      {hasPhotos ? (
                        <>
                          <img src={item.src} alt={`Gallery ${idx+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(7,15,34,0.4) 0%,transparent 60%)', pointerEvents: 'none' }} />
                        </>
                      ) : (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '3rem', marginBottom: 10 }}>{item.emoji}</div>
                          <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: '0.7rem', color: 'var(--primary)', letterSpacing: '0.1em' }}>{item.label}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <button onClick={prev} style={btnStyle('left')}
                onMouseEnter={e=>{e.currentTarget.style.background='var(--card-border)'}}
                onMouseLeave={e=>{e.currentTarget.style.background='rgba(13,27,62,0.95)'}}
              >‹</button>
              <button onClick={next} style={btnStyle('right')}
                onMouseEnter={e=>{e.currentTarget.style.background='var(--card-border)'}}
                onMouseLeave={e=>{e.currentTarget.style.background='rgba(13,27,62,0.95)'}}
              >›</button>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 28 }}>
              {items.map((_, i) => (
                <button key={i} onClick={() => goTo(i)} style={{ width: i===current?28:8, height: 8, borderRadius: 4, border: 'none', background: i===current?'var(--primary)':'rgba(201,168,76,0.25)', cursor: 'pointer', padding: 0, transition: 'all 0.35s ease' }} />
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 14, fontSize: '0.72rem', color: 'rgba(201,168,76,0.5)', letterSpacing: '0.15em' }}>
              {current+1} / {total} {hasPhotos ? 'photos' : '— Add photos via Admin Panel'}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
